/**
 * 自建后端 API 客户端
 * 后端地址默认 https://api.jacvipac.cc ，可用 VITE_PUBLIC_API_BASE 覆盖
 */

const envBase = (
  (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_PUBLIC_API_BASE || ''
).trim();

export const API_BASE = (envBase || 'https://api.jacvipac.cc').replace(/\/+$/, '');
export const API_URL = `${API_BASE}/api`;

const TOKEN_KEY = 'jinan_api_token';
const USER_KEY = 'jinan_api_user';
const REQUEST_TIMEOUT = 20000;

export interface ApiUser {
  id: string;
  email: string;
  phone: string;
  name: string;
  role: string;
  member_level: string;
  verified: boolean;
  balance: number;
  trade_password_set: boolean;
  created_at: string;
}

export class ApiError extends Error {
  status: number;

  constructor(message: string, status = 0) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// ============================================================
// 登录态存储
// ============================================================
export function getToken(): string {
  try {
    return localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

export function getCachedUser(): ApiUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ApiUser;
    return parsed && parsed.id ? parsed : null;
  } catch {
    return null;
  }
}

type AuthListener = (user: ApiUser | null) => void;
const listeners = new Set<AuthListener>();

function emitAuth(user: ApiUser | null) {
  listeners.forEach((fn) => {
    try {
      fn(user);
    } catch {
      // 忽略单个监听器异常
    }
  });
}

export function subscribeAuth(listener: AuthListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function saveSession(token: string, user: ApiUser) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    // ignore
  }
  emitAuth(user);
}

export function clearSession() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    // ignore
  }
  emitAuth(null);
}

// ============================================================
// 请求
// ============================================================
interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  auth?: boolean;
  timeout?: number;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true, timeout = REQUEST_TIMEOUT } = options;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);

  try {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const token = getToken();
    if (auth && token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });

    const text = await res.text();
    let parsed: Record<string, unknown> | null = null;
    if (text) {
      try {
        parsed = JSON.parse(text) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
    }

    if (!res.ok) {
      const message = String(
        (parsed?.error as string)
        || (parsed?.detail as string)
        || text
        || `请求失败(${res.status})`,
      );
      throw new ApiError(message, res.status);
    }

    if (!parsed) throw new ApiError('服务器返回格式异常', res.status);
    return parsed as T;
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') {
      throw new ApiError('网络超时，请稍后重试', 0);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

async function withRetry<T>(fn: () => Promise<T>, attempts = 2): Promise<T> {
  let lastErr: unknown = null;
  for (let i = 0; i < attempts; i += 1) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      const status = (err as ApiError)?.status ?? 0;
      const isNetwork = status === 0 || status >= 500;
      if (!isNetwork || i === attempts - 1) throw err;
      await new Promise((resolve) => setTimeout(resolve, 600 * (i + 1)));
    }
  }
  throw lastErr;
}

// ============================================================
// 鉴权接口
// ============================================================
export const authApi = {
  login: (phone: string, password: string) =>
    withRetry(() => apiRequest<{ token: string; user: ApiUser }>('/auth/login', {
      method: 'POST',
      body: { phone, password },
      auth: false,
    })),

  register: (phone: string, password: string, name?: string) =>
    apiRequest<{ token: string; user: ApiUser }>('/auth/register', {
      method: 'POST',
      body: { phone, password, name },
      auth: false,
    }),

  me: () => apiRequest<{ user: ApiUser; profile: Record<string, unknown> }>('/auth/me'),

  changePassword: (oldPassword: string, newPassword: string) =>
    apiRequest<{ ok: boolean }>('/auth/change-password', {
      method: 'POST',
      body: { old_password: oldPassword, new_password: newPassword },
    }),

  refresh: () => apiRequest<{ token: string }>('/auth/refresh', { method: 'POST', body: {} }),
};